import { useCallback, useState } from 'react';
import { ItemToReturn } from '@src/models/returns';
import { IPolicyValidatorResponse } from '@src/models/api';
import {
  setSelectedItems,
  setUpdatedItem,
  setReturnShipments,
  useReturnStore,
  updateMaxBoxesShipments,
  deleteItemFromShipment,
} from '@src/stores/returnStore';
import { PolicyValidatorErrors, checkFraudType } from '@src/utils';
import { redirectToOrderDetails } from '@src/utils/general';
import { isClientSideOnlyMethod } from '@src/constants/general';

interface UseItemEditRemoveProps {
  selectedItems: ItemToReturn[];
  processPolicyValidator: (
    items: ItemToReturn[],
  ) => Promise<IPolicyValidatorResponse | undefined>;
  setPolicyValidatorAlert: (alert: PolicyValidatorErrors) => void;
  proceedToNextStep: (modifiedItems?: ItemToReturn[]) => void;
  orderDetailsPath: string;
  isMilitaryExchangeGuest: boolean;
}

export const useItemEditRemove = ({
  selectedItems,
  processPolicyValidator,
  setPolicyValidatorAlert,
  proceedToNextStep,
  orderDetailsPath,
  isMilitaryExchangeGuest,
}: UseItemEditRemoveProps) => {
  const [renderRemoveItemModal, setRenderRemoveItemModal] = useState(false);
  const [itemIdToRemove, setItemIdToRemove] = useState<string>('');
  const returnShipments = useReturnStore((state) => state.returnShipments);

  const handlePolicyValidationWithFraud = useCallback(
    async (
      modifiedItems: ItemToReturn[],
      onSuccess: () => void,
      onFraudWarned: () => void,
      isInStoreChange: boolean,
    ) => {
      setPolicyValidatorAlert(PolicyValidatorErrors.NONE);
      if (isInStoreChange) {
        onSuccess();
        return true;
      }
      const policyResponse = await processPolicyValidator(modifiedItems);

      if (!policyResponse) {
        // Validation failed - return false so caller restores state
        return false;
      }

      const verifyDecision = policyResponse?.returnService?.multiVerifyDecision;
      const fraudType = checkFraudType(verifyDecision);

      if (!fraudType) {
        // No fraud - success path
        onSuccess();
        return true;
      }

      if (fraudType === 'DENIED') {
        // DENIED shows modal automatically, return false so caller restores state
        return false;
      }

      // WARNED - update state and show modal, user can continue
      if (onFraudWarned) {
        onFraudWarned();
      }
      return true;
    },
    [processPolicyValidator, setPolicyValidatorAlert],
  );

  const onEditItemSubmit = useCallback(
    async (startingState: ItemToReturn, closeDrawer: () => void) => {
      const isInStoreChange = isClientSideOnlyMethod(
        startingState.returnMethod,
      );
      const wasHandled = await handlePolicyValidationWithFraud(
        selectedItems,
        () => {
          // Success: close drawer, recalculate, and proceed
          updateMaxBoxesShipments(selectedItems);
          closeDrawer();
          proceedToNextStep();
        },
        () => {
          // WARNED: recalculate, close the drawer, modal handles continuation
          updateMaxBoxesShipments(selectedItems);
          closeDrawer();
        },
        isInStoreChange,
      );

      // Restore state if validation failed or DENIED
      if (!wasHandled) {
        setUpdatedItem(startingState);
        closeDrawer();
      }
    },
    [selectedItems, handlePolicyValidationWithFraud, proceedToNextStep],
  );

  const onRemoveItemSubmit = useCallback(
    (selectedItemId: string) => {
      setItemIdToRemove(selectedItemId);
      setRenderRemoveItemModal(true);
    },
    [setItemIdToRemove, setRenderRemoveItemModal],
  );

  const onConfirmRemoveItem = useCallback(async () => {
    const originalReturnShipments = returnShipments || [];
    const originalSelectedItems = [...selectedItems];

    const modifiedSelectedItems = selectedItems.filter(
      (item) => item.lineItemId !== itemIdToRemove,
    );
    if (modifiedSelectedItems.length === 0) {
      redirectToOrderDetails(orderDetailsPath, isMilitaryExchangeGuest);
      return;
    }

    // Find the item being removed
    const removedItem = selectedItems.find(
      (item) => item.lineItemId === itemIdToRemove,
    );

    // Only need HRTV if:
    // 1. We're removing a non-client-side item (UPS/LORP/SORIS) AND
    // 2. There are still non-client-side items remaining after removal
    const isRemovingClientSideItem = isClientSideOnlyMethod(
      removedItem?.returnMethod,
    );
    const hasRemainingApiItems = modifiedSelectedItems.some(
      (item) => !isClientSideOnlyMethod(item.returnMethod),
    );
    const isInStoreChange = isRemovingClientSideItem || !hasRemainingApiItems;

    const wasHandled = await handlePolicyValidationWithFraud(
      modifiedSelectedItems,
      () => {
        // Success: update state and proceed
        setSelectedItems(modifiedSelectedItems);
        updateMaxBoxesShipments(modifiedSelectedItems);
        proceedToNextStep(modifiedSelectedItems);
        if (isInStoreChange) {
          deleteItemFromShipment(itemIdToRemove);
        }
      },
      () => {
        // WARNED: update state with modified items, modal will let user continue
        setSelectedItems(modifiedSelectedItems);
        updateMaxBoxesShipments(modifiedSelectedItems);
      },
      isInStoreChange,
    );

    // Restore state if validation failed or DENIED
    if (!wasHandled) {
      setSelectedItems(originalSelectedItems);
      setReturnShipments(originalReturnShipments);
    }
    setRenderRemoveItemModal(false);
    setItemIdToRemove('');
  }, [
    selectedItems,
    returnShipments,
    handlePolicyValidationWithFraud,
    proceedToNextStep,
    orderDetailsPath,
    isMilitaryExchangeGuest,
    setItemIdToRemove,
    itemIdToRemove,
    setRenderRemoveItemModal,
  ]);

  const onCancelRemoveItem = useCallback(() => {
    setRenderRemoveItemModal(false);
    setItemIdToRemove('');
  }, [setRenderRemoveItemModal, setItemIdToRemove]);

  return {
    onEditItemSubmit,
    onRemoveItemSubmit,
    renderRemoveItemModal,
    onCancelRemoveItem,
    onConfirmRemoveItem,
  };
};
